"use client";

import { c as _c } from "react/compiler-runtime";
import type { FocusEvent, KeyboardEvent } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { MdClose, MdEdit } from "react-icons/md";
import { Checkbox, Label } from "@/components/ui";
import { APISearchSelect, APISearchSelectAction, APISearchSelectAnchor, APISearchSelectContent, APISearchSelectInput } from "@/components/ui/select/api-search-select";
import { useDebounce } from "@/hooks/use-debounce";
import type { PatientReportedMedicationEntry } from "@/lib/patient-reported-medications";
import { cx, focusRing } from "@/lib/tailwind-utils";
import { type PatientReportedMedicationSuggestion, RXTERMS_MIN_SEARCH_LENGTH, usePatientReportedMedicationSuggestions } from "@/api/provider/medications/patient-reported-suggestions";
const STRENGTH_HINT = "Choose the strength and form the patient reports taking, or add the medication without one.";

// Rows are composed below, so the select never formats an option itself
const formatSuggestion = (suggestion: PatientReportedMedicationSuggestion) => ({
  id: suggestion.name,
  name: suggestion.name
});
interface PatientReportedMedicationInputProps {
  medications: PatientReportedMedicationEntry[];
  onChange: (medications: PatientReportedMedicationEntry[]) => void;
  disabled?: boolean;
  inputId?: string;
  placeholder?: string;
  /** Shown in the add box once at least one medication is listed. */
  addAnotherPlaceholder?: string;
  className?: string;
  hasError?: boolean;
  errorMessage?: string;
  /** Select a medication concept immediately, without asking for strength/form. */
  conceptOnly?: boolean;
  /** Offers a clear, structured way to answer a required question with no items. */
  noneOptionLabel?: string;
}
export default function PatientReportedMedicationInput(t0) {
  const $ = _c(111);
  const {
    medications,
    onChange,
    disabled,
    inputId,
    placeholder: t1,
    addAnotherPlaceholder: t2,
    className,
    hasError,
    errorMessage,
    conceptOnly: t3,
    noneOptionLabel
  } = t0;
  const placeholder = t1 === undefined ? "Add medication" : t1;
  const addAnotherPlaceholder = t2 === undefined ? "Add another" : t2;
  const conceptOnly = t3 === undefined ? false : t3;
  const [inputValue, setInputValue] = useState("");
  const [selectedDrugName, setSelectedDrugName] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const previousEditingIndex = useRef(null);
  const committedOnBlur = useRef(false);
  const statusId = useId();
  const noneOptionId = useId();
  const editErrorId = useId();
  const t4 = useDebounce(inputValue, 500);
  let t5;
  if ($[0] !== t4) {
    t5 = t4.trim();
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const debouncedSearch = t5;
  let t6;
  if ($[2] !== inputValue) {
    t6 = inputValue.trim();
    $[2] = inputValue;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const trimmedInput = t6;
  const {
    data: t7,
    isFetching,
    isError
  } = usePatientReportedMedicationSuggestions(debouncedSearch);
  const suggestions = t7 === undefined ? [] : t7;
  const editingMedication = editingIndex === null ? null : medications[editingIndex] ?? null;
  let t8;
  if ($[4] !== editingIndex || $[5] !== medications) {
    let t9;
    if ($[7] !== editingIndex) {
      t9 = (_, index) => index !== editingIndex;
      $[7] = editingIndex;
      $[8] = t9;
    } else {
      t9 = $[8];
    }
    t8 = new Set(medications.filter(t9).map(_temp));
    $[4] = editingIndex;
    $[5] = medications;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const exactMedicationSet = t8;
  let t9;
  if ($[9] !== selectedDrugName) {
    t9 = suggestion => suggestion.name === selectedDrugName;
    $[9] = selectedDrugName;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const selectedDrug = suggestions.find(t9);
  const searchIsCurrent = debouncedSearch === trimmedInput;
  const showSuggestions = searchIsCurrent && debouncedSearch.length >= RXTERMS_MIN_SEARCH_LENGTH && !selectedDrug;
  const showOptions = !!selectedDrug;
  const noMatches = showSuggestions && !isFetching && suggestions.length === 0;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setEditingIndex(null);
      setInputValue("");
      setSelectedDrugName(null);
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const cancelEdit = t10;
  let t11;
  if ($[12] !== editingIndex || $[13] !== editingMedication || $[14] !== exactMedicationSet || $[15] !== medications || $[16] !== onChange) {
    t11 = (value, t12, t13) => {
      const conceptRxId = t12 === undefined ? null : t12;
      const strengthFormRxId = t13 === undefined ? null : t13;
      const trimmed = value.trim();
      if (editingIndex !== null && editingMedication) {
        if (!trimmed) {
          cancelEdit();
          return;
        }
        if (exactMedicationSet.has(trimmed.toLowerCase())) {
          return;
        }
        const unchanged = trimmed === editingMedication.value.trim() && conceptRxId === null && strengthFormRxId === null;
        onChange(medications.map((medication_0, index_0) => index_0 === editingIndex ? unchanged ? medication_0 : {
          value: trimmed,
          conceptRxId,
          strengthFormRxId
        } : medication_0));
        cancelEdit();
        return;
      }
      if (!trimmed || exactMedicationSet.has(trimmed.toLowerCase())) {
        return;
      }
      onChange([...medications, {
        value: trimmed,
        conceptRxId,
        strengthFormRxId
      }]);
      setInputValue("");
      setSelectedDrugName(null);
    };
    $[12] = editingIndex;
    $[13] = editingMedication;
    $[14] = exactMedicationSet;
    $[15] = medications;
    $[16] = onChange;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const addMedication = t11;
  let t12;
  let t13;
  if ($[18] !== editingIndex) {
    t12 = () => {
      if (editingIndex === null) {
        return;
      }
      const handleEscape = event => {
        if (event.key !== "Escape" || !containerRef.current?.contains(event.target as Node)) {
          return;
        }
        event.stopPropagation();
        setEditingIndex(null);
        setInputValue("");
        setSelectedDrugName(null);
      };
      window.addEventListener("keydown", handleEscape, true);
      return () => window.removeEventListener("keydown", handleEscape, true);
    };
    t13 = [editingIndex];
    $[18] = editingIndex;
    $[19] = t12;
    $[20] = t13;
  } else {
    t12 = $[19];
    t13 = $[20];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[21] !== editingIndex) {
    t14 = () => {
      if (previousEditingIndex.current !== null && editingIndex === null && !committedOnBlur.current) {
        inputRef.current?.focus();
      }
      previousEditingIndex.current = editingIndex;
      committedOnBlur.current = false;
    };
    t15 = [editingIndex];
    $[21] = editingIndex;
    $[22] = t14;
    $[23] = t15;
  } else {
    t14 = $[22];
    t15 = $[23];
  }
  useEffect(t14, t15);
  let t16;
  if ($[24] !== medications) {
    t16 = index_1 => {
      setEditingIndex(index_1);
      setInputValue(medications[index_1].value);
      setSelectedDrugName(null);
    };
    $[24] = medications;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const startEdit = t16;
  let t17;
  if ($[26] !== editingIndex || $[27] !== medications || $[28] !== onChange) {
    t17 = index_2 => {
      if (editingIndex !== null) {
        cancelEdit();
      }
      onChange(medications.filter((__0, i) => i !== index_2));
    };
    $[26] = editingIndex;
    $[27] = medications;
    $[28] = onChange;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const removeMedication = t17;
  let t18;
  if ($[30] !== addMedication || $[31] !== trimmedInput) {
    t18 = event_0 => {
      committedOnBlur.current = false;
      if (event_0.key === "Enter" && trimmedInput) {
        event_0.preventDefault();
        addMedication(trimmedInput);
      } else {
        if (event_0.key === "Escape") {
          setSelectedDrugName(null);
        }
      }
    };
    $[30] = addMedication;
    $[31] = trimmedInput;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const handleKeyDown = t18;
  let t19;
  if ($[33] !== addMedication || $[34] !== editingIndex || $[35] !== trimmedInput) {
    t19 = event_1 => {
      if (containerRef.current?.contains(event_1.relatedTarget)) {
        return;
      }
      if (editingIndex !== null) {
        committedOnBlur.current = true;
      }
      addMedication(trimmedInput);
    };
    $[33] = addMedication;
    $[34] = editingIndex;
    $[35] = trimmedInput;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const handleBlur = t19;
  let t20;
  if ($[37] !== exactMedicationSet) {
    t20 = value_0 => exactMedicationSet.has(value_0.trim().toLowerCase());
    $[37] = exactMedicationSet;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const isDuplicate = t20;
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = term => {
      setInputValue(term);
      setSelectedDrugName(null);
    };
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  const handleSearchTermChange = t21;
  let t22;
  if ($[40] !== className) {
    t22 = cx("space-y-2", className);
    $[40] = className;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const T0 = APISearchSelect;
  const t23 = formatSuggestion;
  const t24 = disabled && "cursor-not-allowed opacity-60";
  let t25;
  if ($[42] !== t24) {
    t25 = cx("flex min-h-11 flex-wrap items-center gap-1 rounded-lg border border-border bg-background p-1", t24);
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== disabled || $[45] !== editErrorId || $[46] !== editingIndex || $[47] !== handleBlur || $[48] !== handleKeyDown || $[49] !== hasError || $[50] !== inputId || $[51] !== inputValue.length || $[52] !== medications || $[53] !== removeMedication || $[54] !== startEdit || $[55] !== statusId) {
    t26 = medications.map((medication_1, index_3) => index_3 === editingIndex ? <span key={`${medication_1.value}-${index_3}`} className="flex min-h-7 max-w-full items-center gap-1 rounded-md bg-background pr-1.5 ring-2 ring-primary"><APISearchSelectInput ref={inputRef} hideSearchIcon={true} onKeyDown={handleKeyDown} onBlur={handleBlur} disabled={disabled} autoFocus={true} id={inputId} aria-label={`Editing ${medication_1.value}`} aria-invalid={hasError || undefined} aria-describedby={hasError ? `${statusId} ${editErrorId}` : statusId} style={{
        width: `calc(${Math.max(inputValue.length, 8) + 2}ch + 1rem)`
      }} className="min-w-0 max-w-full border-0 bg-transparent px-2 py-0 text-caption text-foreground shadow-none outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0" /><button type="button" className={cx("-my-1 flex size-6 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground", focusRing)} onMouseDown={_temp2} onClick={cancelEdit} aria-label="Cancel edit" title="Cancel edit"><MdClose className="h-3.5 w-3.5" /></button></span> : <span key={`${medication_1.value}-${index_3}`} className="group flex min-h-7 items-center gap-1 rounded-md bg-accent px-2 text-caption text-accent-foreground"><button type="button" className={cx("flex items-center gap-1 rounded-sm hover:underline disabled:cursor-not-allowed disabled:no-underline", focusRing)} onClick={() => startEdit(index_3)} disabled={disabled} aria-label={`Edit ${medication_1.value}`} title="Edit"><span>{medication_1.value}</span><MdEdit className="h-3 w-3 opacity-50 group-hover:opacity-100" /></button><button type="button" className="text-accent-foreground/70 hover:text-accent-foreground disabled:cursor-not-allowed" onClick={() => removeMedication(index_3)} disabled={disabled} aria-label={`Remove ${medication_1.value}`}><MdClose className="h-3.5 w-3.5" /></button></span>);
    $[44] = disabled;
    $[45] = editErrorId;
    $[46] = editingIndex;
    $[47] = handleBlur;
    $[48] = handleKeyDown;
    $[49] = hasError;
    $[50] = inputId;
    $[51] = inputValue.length;
    $[52] = medications;
    $[53] = removeMedication;
    $[54] = startEdit;
    $[55] = statusId;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== addAnotherPlaceholder || $[58] !== disabled || $[59] !== editingIndex || $[60] !== errorMessage || $[61] !== handleBlur || $[62] !== handleKeyDown || $[63] !== hasError || $[64] !== inputId || $[65] !== medications.length || $[66] !== placeholder || $[67] !== statusId) {
    t27 = editingIndex === null && <div className="min-w-40 flex-1"><APISearchSelectInput ref={inputRef} id={inputId} hideSearchIcon={true} onKeyDown={handleKeyDown} onBlur={handleBlur} disabled={disabled} placeholder={medications.length === 0 ? placeholder : addAnotherPlaceholder} aria-invalid={hasError || undefined} aria-describedby={statusId} hasError={hasError} errorMessage={errorMessage} className="border-0 bg-transparent p-1 text-caption shadow-none focus:ring-0 focus-visible:ring-0" /></div>;
    $[57] = addAnotherPlaceholder;
    $[58] = disabled;
    $[59] = editingIndex;
    $[60] = errorMessage;
    $[61] = handleBlur;
    $[62] = handleKeyDown;
    $[63] = hasError;
    $[64] = inputId;
    $[65] = medications.length;
    $[66] = placeholder;
    $[67] = statusId;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t25 || $[70] !== t26 || $[71] !== t27) {
    t28 = <APISearchSelectAnchor className={t25}>{t26}{t27}</APISearchSelectAnchor>;
    $[69] = t25;
    $[70] = t26;
    $[71] = t27;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  const t29 = !disabled && <APISearchSelectContent className="max-h-64" listClassName="max-h-64">{showSuggestions && suggestions.map(suggestion_0 => <APISearchSelectAction key={suggestion_0.name} onSelect={() => {
      if (conceptOnly) {
        addMedication(suggestion_0.name, suggestion_0.conceptRxId ?? null);
      } else {
        setSelectedDrugName(suggestion_0.name);
      }
    }}>{suggestion_0.name}</APISearchSelectAction>)}{noMatches && <div aria-hidden={true} className="px-2 py-1.5 text-caption text-muted-foreground">No matching medications found.</div>}{showOptions && selectedDrug && <><div aria-hidden={true} className="px-2 py-1.5 text-caption text-muted-foreground">{STRENGTH_HINT}</div>{selectedDrug.options.length > 0 && <APISearchSelectAction disabled={isDuplicate(selectedDrug.name)} onSelect={() => addMedication(selectedDrug.name, selectedDrug.conceptRxId ?? null)}>Add {selectedDrug.name} without strength or form</APISearchSelectAction>}{(selectedDrug.options.length > 0 ? selectedDrug.options : [{
        value: selectedDrug.name,
        strengthFormRxId: null
      }]).map(option => <APISearchSelectAction key={option.value} disabled={isDuplicate(option.value)} onSelect={() => addMedication(option.value, selectedDrug.conceptRxId ?? null, option.strengthFormRxId)}>{option.value}</APISearchSelectAction>)}<APISearchSelectAction onSelect={() => setSelectedDrugName(null)}>Back to medication matches</APISearchSelectAction></>}{trimmedInput && <APISearchSelectAction disabled={isDuplicate(trimmedInput)} onSelect={() => addMedication(trimmedInput)}>{`${editingMedication ? "Save" : "Add"} “${trimmedInput}” as entered`}</APISearchSelectAction>}</APISearchSelectContent>;
  let t30;
  if ($[73] !== T0 || $[74] !== handleSearchTermChange || $[75] !== inputValue || $[76] !== t23 || $[77] !== t28 || $[78] !== t29) {
    t30 = <T0 searchTerm={inputValue} onSearchTermChange={handleSearchTermChange} formatOption={t23}>{t28}{t29}</T0>;
    $[73] = T0;
    $[74] = handleSearchTermChange;
    $[75] = inputValue;
    $[76] = t23;
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== editErrorId || $[81] !== editingIndex || $[82] !== errorMessage || $[83] !== hasError) {
    t31 = editingIndex !== null && hasError && <p id={editErrorId} className="text-status-error-text text-caption">{errorMessage}</p>;
    $[80] = editErrorId;
    $[81] = editingIndex;
    $[82] = errorMessage;
    $[83] = hasError;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] !== addMedication || $[86] !== disabled || $[87] !== medications.length || $[88] !== noneOptionId || $[89] !== noneOptionLabel) {
    t32 = noneOptionLabel && medications.length === 0 && !disabled && <div className="flex items-center gap-2"><Checkbox id={noneOptionId} checked={false} onCheckedChange={checked => {
        if (checked) {
          addMedication("None");
        }
      }} /><Label htmlFor={noneOptionId} className="cursor-pointer font-normal text-caption">{noneOptionLabel}</Label></div>;
    $[85] = addMedication;
    $[86] = disabled;
    $[87] = medications.length;
    $[88] = noneOptionId;
    $[89] = noneOptionLabel;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== noMatches) {
    t33 = noMatches && <span className="sr-only">No matching medications found.</span>;
    $[91] = noMatches;
    $[92] = t33;
  } else {
    t33 = $[92];
  }
  let t34;
  if ($[93] !== showOptions) {
    t34 = showOptions && <span className="sr-only">{STRENGTH_HINT}</span>;
    $[93] = showOptions;
    $[94] = t34;
  } else {
    t34 = $[94];
  }
  let t35;
  if ($[95] !== isFetching) {
    t35 = isFetching && <span className="text-muted-foreground">Searching medications…</span>;
    $[95] = isFetching;
    $[96] = t35;
  } else {
    t35 = $[96];
  }
  let t36;
  if ($[97] !== isError) {
    t36 = isError && <span className="text-status-error-text">Medication suggestions are unavailable. You can still add what the patient reported.</span>;
    $[97] = isError;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  let t37;
  if ($[99] !== statusId || $[100] !== t33 || $[101] !== t34 || $[102] !== t35 || $[103] !== t36) {
    t37 = <div id={statusId} aria-live="polite" className="text-caption">{t33}{t34}{t35}{t36}</div>;
    $[99] = statusId;
    $[100] = t33;
    $[101] = t34;
    $[102] = t35;
    $[103] = t36;
    $[104] = t37;
  } else {
    t37 = $[104];
  }
  let t38;
  if ($[105] !== t22 || $[106] !== t30 || $[107] !== t31 || $[108] !== t32 || $[109] !== t37) {
    t38 = <div ref={containerRef} className={t22}>{t30}{t31}{t32}{t37}</div>;
    $[105] = t22;
    $[106] = t30;
    $[107] = t31;
    $[108] = t32;
    $[109] = t37;
    $[110] = t38;
  } else {
    t38 = $[110];
  }
  return t38;
}
function _temp2(event_2) {
  return event_2.preventDefault();
}
function _temp(medication) {
  return medication.value.trim().toLowerCase();
}